import { useEffect, useMemo, useRef, useState } from "react";
import { Card, Radio, Select } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const ALGORITHMS = ["SHA-1", "SHA-256", "SHA-384", "SHA-512"];

const FORMAT_HEX = "hex";
const FORMAT_HEX_UPPER = "hex_upper";
const FORMAT_BASE64 = "base64";

const MODE_TEXT = "text";
const MODE_FILE = "file";

const DEFAULT_INPUT = "Quick Tools for Day-to-Day Developer Use";
const MAX_FILE_BYTES = 512 * 1024 * 1024;

const toHex = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let out = "";
  for (let i = 0; i < bytes.length; i += 1) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
};

const toBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const formatDigest = (buffer, format) => {
  if (format === FORMAT_BASE64) return toBase64(buffer);
  const hex = toHex(buffer);
  return format === FORMAT_HEX_UPPER ? hex.toUpperCase() : hex;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const HashGenerator = () => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(MODE_TEXT);
  const [input, setInput] = useState(DEFAULT_INPUT);
  const [algorithm, setAlgorithm] = useState("SHA-256");
  const [format, setFormat] = useState(FORMAT_HEX);
  const [digest, setDigest] = useState(null);
  const [error, setError] = useState(null);
  const [compare, setCompare] = useState("");
  const [file, setFile] = useState(null);
  const [fileComputing, setFileComputing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (mode !== MODE_TEXT) return undefined;
    let cancelled = false;
    if (input === "") {
      setDigest(null);
      setError(null);
      return () => {
        cancelled = true;
      };
    }
    const subtle = window.crypto && window.crypto.subtle;
    if (!subtle) {
      setError("hash_generator_error_unsupported");
      setDigest(null);
      return () => {
        cancelled = true;
      };
    }
    const bytes = new TextEncoder().encode(input);
    subtle
      .digest(algorithm, bytes)
      .then((buffer) => {
        if (!cancelled) {
          setDigest(buffer);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("hash_generator_error_compute");
          setDigest(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [input, algorithm, mode]);

  useEffect(() => {
    if (mode !== MODE_FILE) return undefined;
    if (!file) {
      setDigest(null);
      setError(null);
      setFileComputing(false);
      return undefined;
    }
    const subtle = window.crypto && window.crypto.subtle;
    if (!subtle) {
      setError("hash_generator_error_unsupported");
      setDigest(null);
      setFileComputing(false);
      return undefined;
    }
    let cancelled = false;
    setFileComputing(true);
    setError(null);
    (async () => {
      try {
        const buffer = await file.arrayBuffer();
        if (cancelled) return;
        const result = await subtle.digest(algorithm, buffer);
        if (cancelled) return;
        setDigest(result);
        setError(null);
      } catch {
        if (cancelled) return;
        setError("hash_generator_error_file_compute");
        setDigest(null);
      } finally {
        if (!cancelled) setFileComputing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file, algorithm, mode]);

  const output = useMemo(() => {
    if (!digest) return "";
    return formatDigest(digest, format);
  }, [digest, format]);

  const compareStatus = useMemo(() => {
    const expected = compare.trim();
    if (!expected || !output) return null;
    return expected.toLowerCase() === output.toLowerCase() ? "match" : "no_match";
  }, [compare, output]);

  const copy = async () => {
    if (!output) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => {
    setInput("");
    setCompare("");
  };

  const handleModeChange = (event) => {
    const next = event.target.value;
    setMode(next);
    setDigest(null);
    setError(null);
    setCompare("");
  };

  const acceptFile = (incoming) => {
    if (!incoming) return;
    if (incoming.size > MAX_FILE_BYTES) {
      setFile(null);
      setDigest(null);
      setError("hash_generator_error_file_too_large");
      return;
    }
    setError(null);
    setFile(incoming);
  };

  const handleFileSelect = (event) => {
    const selected = event.target.files && event.target.files[0];
    acceptFile(selected);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    const dropped = event.dataTransfer.files && event.dataTransfer.files[0];
    acceptFile(dropped);
  };

  const removeFile = () => {
    setFile(null);
    setDigest(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("hash_generator_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("hash_generator_intro")}</p>
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("hash_generator_source_label")}
                </label>
                <Radio.Group value={mode} onChange={handleModeChange}>
                  <Radio.Button value={MODE_TEXT}>
                    {t("hash_generator_mode_text")}
                  </Radio.Button>
                  <Radio.Button value={MODE_FILE}>
                    {t("hash_generator_mode_file")}
                  </Radio.Button>
                </Radio.Group>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("hash_generator_algorithm_label")}
                </label>
                <Select
                  value={algorithm}
                  onChange={setAlgorithm}
                  style={{ minWidth: 160 }}
                  options={ALGORITHMS.map((alg) => ({
                    value: alg,
                    label: alg,
                  }))}
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("hash_generator_output_format_label")}
                </label>
                <Radio.Group
                  value={format}
                  onChange={(e) => setFormat(e.target.value)}
                >
                  <Radio.Button value={FORMAT_HEX}>
                    {t("hash_generator_format_hex")}
                  </Radio.Button>
                  <Radio.Button value={FORMAT_HEX_UPPER}>
                    {t("hash_generator_format_hex_upper")}
                  </Radio.Button>
                  <Radio.Button value={FORMAT_BASE64}>
                    {t("hash_generator_format_base64")}
                  </Radio.Button>
                </Radio.Group>
              </div>
            </div>

            {mode === MODE_TEXT && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("hash_generator_input_label")}
                </label>
                <textarea
                  rows={6}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                  placeholder={t("hash_generator_input_placeholder")}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                />
                <div className="flex flex-wrap gap-3">
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("hash_generator_clear")}
                  </StandardButton>
                  <span className="text-xs text-gray-500 self-center">
                    {t("hash_generator_byte_count", {
                      count: new TextEncoder().encode(input).length,
                    })}
                  </span>
                </div>
              </div>
            )}

            {mode === MODE_FILE && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700">
                  {t("hash_generator_file_label")}
                </label>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      if (fileInputRef.current) fileInputRef.current.click();
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  aria-label={t("hash_generator_file_drop_aria")}
                  className={`flex flex-col items-center justify-center text-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                    dragActive
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/50"
                  }`}
                >
                  <p className="text-sm text-gray-700 font-medium">
                    {dragActive
                      ? t("hash_generator_file_drop_active")
                      : t("hash_generator_file_drop")}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("hash_generator_file_size_limit", {
                      size: formatFileSize(MAX_FILE_BYTES),
                    })}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  aria-hidden="true"
                />
                {file && (
                  <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-800 font-medium break-all">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatFileSize(file.size)}
                      </span>
                    </div>
                    <StandardButton
                      onClick={removeFile}
                      type="secondary"
                      variant="outlined"
                      size="small"
                    >
                      {t("hash_generator_file_remove")}
                    </StandardButton>
                  </div>
                )}
                {fileComputing && (
                  <p
                    className="text-sm text-indigo-700"
                    role="status"
                    aria-live="polite"
                  >
                    {t("hash_generator_file_computing")}
                  </p>
                )}
              </div>
            )}

            {error && (
              <p className="text-sm text-red-600">{t(error)}</p>
            )}

            {!error && output && !fileComputing && (
              <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                <div className="flex items-center justify-between gap-3">
                  <strong className="text-indigo-700">
                    {t("hash_generator_output_label", { algorithm })}
                  </strong>
                  <StandardButton
                    onClick={copy}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
                <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">
                  {output}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("hash_generator_compare_label")}
              </label>
              <input
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                placeholder={t("hash_generator_compare_placeholder")}
                value={compare}
                onChange={(e) => setCompare(e.target.value)}
              />
              {compareStatus === "match" && (
                <p className="text-sm text-emerald-700">
                  {t("hash_generator_compare_match")}
                </p>
              )}
              {compareStatus === "no_match" && (
                <p className="text-sm text-red-600">
                  {t("hash_generator_compare_no_match")}
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("hash_generator_note_algorithms")}</p>
              <p>{t("hash_generator_note_sha1")}</p>
              <p>{t("hash_generator_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default HashGenerator;
